import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getPayorClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type { AddAcceptedPayorRequest, PayorPlanResponse } from '@vagusx/clients';

/** Server-side search of the payor catalog (debounce on the caller side). */
export function usePayorSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.payors.search(query),
    queryFn: () => getPayorClient().search(query),
    enabled: query.length > 0,
  });
}

/** List plans for a specific payor. */
export function usePayorPlans(payorId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.payors.plans(payorId ?? ''),
    queryFn: () => getPayorClient().getPlans(payorId!),
    enabled: !!payorId,
  });
}

/**
 * Fetch catalog plans for multiple payors (used to expand "all plans" rows).
 * Returns a Map<payorId, PayorPlanResponse[]>.
 */
export function useAllAcceptedPayorPlans(payorIds: string[]): {
  data: Map<string, PayorPlanResponse[]> | undefined;
} {
  const queries = useQueries({
    queries: payorIds.map((payorId) => ({
      queryKey: queryKeys.payors.plans(payorId),
      queryFn: () => getPayorClient().getPlans(payorId),
      enabled: payorIds.length > 0,
    })),
  });

  const allDone = queries.every((q) => q.isSuccess);
  if (!allDone) return { data: undefined };

  const map = new Map<string, PayorPlanResponse[]>();
  for (let i = 0; i < payorIds.length; i++) {
    const plans = queries[i]?.data;
    if (plans) map.set(payorIds[i]!, plans);
  }
  return { data: map };
}

/** List accepted payors for the current organization. */
export function useAcceptedPayors(includeInactive = false) {
  return useQuery({
    queryKey: [...queryKeys.payors.accepted(), { includeInactive }] as const,
    queryFn: () => getPayorClient().listAccepted(includeInactive),
  });
}

/** Reactivate all archived rows for a payor (bulk). */
export function useReactivatePayor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payorId: string) => getPayorClient().reactivatePayor(payorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
      toast.success('Payor reactivated');
    },
  });
}

/** Add an accepted payor. */
export function useAddAcceptedPayor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AddAcceptedPayorRequest) => getPayorClient().addAccepted(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
      toast.success('Payor added');
    },
  });
}

/** Remove (deactivate) an accepted payor. */
export function useRemoveAcceptedPayor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getPayorClient().removeAccepted(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payors.all });
      toast.success('Payor deactivated');
    },
  });
}
